import ViewSettingsControl from '@components/FilterV3/components/ColumnsVisibility/ViewSettingsControl';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { clearFiltersCreatedByCreator } from '../../../../components/Crud/filters/components/DynamicOptionsSelectorsCreator/hooks/useDynamicOptionsSelectorsCreator';
import useDateRangeFilter from '../../../../components/Crud/filters/hooks/dateRangeFilter/useDateRangeFilter';
import useMultiDynamicOptionsSelector from '../../../../components/Crud/filters/hooks/multiOptionsSelectorFilter/useMultiDynamicOptionsSelector/useMultiDynamicOptionsSelector';
import useMultiStaticOptionsSelector from '../../../../components/Crud/filters/hooks/multiOptionsSelectorFilter/useMultiStaticOptionsSelector';
import useNumberRangeFilter from '../../../../components/Crud/filters/hooks/numberRangeFilter/useNumberRangeFilter';
import useRadioFilter from '../../../../components/Crud/filters/hooks/radioFilter/useRadioFilter';
import useTextFilter from '../../../../components/Crud/filters/hooks/textFilter/useTextFilter';
import useBrandsFilter from '../../../../components/Crud/filters/hooks/useBrandsFilter/useBrandsFilter';
import {
  FilterOptionV3,
  MultiDynamicOptionsCreatorFilter,
  PossibleFilters,
} from '../../../../components/Crud/filters/sharedBetweenFilters/types';
import { DateRangeFilterToFilterV3 } from '../../../../components/FilterV3/components/DateRangeFilterV2/components/filter/components/DateRangeFilterToFilterV3';
import useDateRangeFilterWithDefaultValues from '../../../../components/FilterV3/components/DateRangeFilterV2/components/filter/useDateRangeFilterWithDefaultValues';
import { DateShortcut } from '../../../../components/FilterV3/components/DateRangeFilterV2/dateRangeFilter.types';
import FixedFilterChip from '../../../../components/FilterV3/components/FixedFilterChip/FixedFilterChip';
import { NumberRangeFilterTypes } from '../../../../components/FilterV3/components/NumberRangeFilter/numberRangeFilter.types';
import useWidth from '../../../../components/Hooks/useWidth';
import { Column } from '../../../../components/ListV2/components/table/VirtualizedTable.types';
import SearchField from '../../../../components/v2/ui/FilterBar/components/SearchField/SearchField';
import useDefaultFiltersToList from '../../../../hooks/useDefaultFiltersToList';
import useIsFirstRender from '../../../../hooks/useIsFirstRender';
import useSearchParams from '../../../../hooks/useSearchParams';
import LangEnum from '../../../../lang/LangEnum';
import { getFindClassificationTypesKey } from '../../../../services/modules/classificationTypes/classificationTypes.keys';
import classificationTypesRepository from '../../../../services/modules/classificationTypes/classificationTypes.repository';
import {
  getFindCategoriesFilterOptionsKey,
  getFindCitiesFilterOptionsKey,
  getFindClassificationsFilterOptionsKey,
  getFindCollectionsFilterOptionsKey,
  getFindColorsFilterOptionsKey,
  getFindConsultantsFilterOptionsKey,
  getFindCustomerGroupsFilterOptionsKey,
  getFindCustomersFilterOptionsKey,
  getFindDeliveryWindowsFilterOptionsKey,
  getFindOrderVouchersFilterOptionsKey,
  getFindOrdersFilterOptionsKey,
  getFindParentOrdersFilterOptionsKey,
  getFindProductsFilterOptionsKey,
  getFindSalesRepresentativesFilterOptionsKey,
  getFindSellersFilterOptionsKey,
  getFindStatesFilterOptionsKey,
  getFindSuggestedCopiesFilterOptionsKey,
} from '../../../../services/modules/filterOptions/filterOptions.keys';
import filterOptionsRepository from '../../../../services/modules/filterOptions/repositories/filterOptions.repository';
import { getFindOrderConditionsClassificationTypesKey } from '../../../../services/modules/orderCondition/orderCondition.keys';
import { findOrderConditionsClassificationTypes } from '../../../../services/modules/orderCondition/orderCondition.repository';
import { useGetMaxSubtotal } from '../../../../services/modules/orders/orders.queries';
import {
  ColumnsPreferenceRecognizedLists,
  FilterPreferenceRecognizedLists,
} from '../../../../services/modules/userContextPreferences/userContextPreferences.types';
import { useAppEnvironmentStore } from '../../../../stores/AppEnvironment/appEnvironment.store';
import { selectIsInStoreEnvironment } from '../../../../stores/AppEnvironment/appEnvironment.store.selectors';
import { CustomerStatusEnum } from '../../../../types/Enums';
import { FilterTestIds } from '../../../../utils/testIds/Filter.testIds';
import { OrderListTestIds } from '../../../../utils/testIds/OrderList.testIds';
import { CLASSIFICATIONS_PREFIX } from '../../../classifications/classification-filter.utils';
import { ClassificationTypeEntityEnum } from '../../../classifications/classification.types';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { CUSTOMERS_IDS_KEYS } from '../../../customer/pages/CustomerListPage/utils/customerFilter.utils';
import productAvailabilityOptions from '../../../products/options/productAvailabilityOptions';
import useCanReadSalesRepresentatives from '../../../sales-representative/hooks/useCanReadSalesRepresentatives';
import { useTranslation } from '../../../translation/TranslationContext';
import { useAbility } from '../../../users/AbilityContext';
import useExportMultipleOrdersCsv from '../../export/V2/hooks/useExportMultipleOrdersCsv';
import useOrderStatusOptions from '../../hooks/useOrderStatusOptions';
import { OrderListTabName, getOrderStatusByTab } from '../../list/orderListTabs';
import { OrderListWithBaseItem, synchronizedTypesSelect } from '../../order.types';
import { orderGatewayPaymentStatusOptions } from '../../payment/enums/orderPaymentGatewayStatus.enum';
import { orderSignatureLangEnumOptions } from '../options/orderSignatureFilter.options';
import {
  BRAND_ENVIRONMENT_ONLY_FILTER_KEYS,
  BRAND_ENVIRONMENT_ONLY_FILTER_PREFIXES,
  CUSTOMER_FILTER_KEYS,
  ORDERS_FILTERS_KEYS,
  ORDER_CONDITION_CLASSIFICATIONS_PREFIX,
  ORDER_CUSTOMER_CLASSIFICATIONS_PREFIX,
} from '../orderFilter.utils';

interface UseOrderFilterV2Props {
  selectedTab: OrderListTabName;
  selectedIds?: string[];
  columns: Column<OrderListWithBaseItem>[];
  isDrawerOpen?: boolean;
  setIsDrawerOpen?: Dispatch<SetStateAction<boolean>>;
  list?: FilterPreferenceRecognizedLists;
  customerIds?: string[];
  includeDateFilter?: boolean;
}

export const useOrderFilterV2 = ({
  selectedTab,
  selectedIds,
  columns,
  isDrawerOpen,
  setIsDrawerOpen,
  list: listProp,
  customerIds,
  includeDateFilter = true,
}: UseOrderFilterV2Props) => {
  const ability = useAbility();
  const canNotReadSalesRepresentatives = !useCanReadSalesRepresentatives();
  const canNotReadCustomerGroups = ability.cannot('read', 'customer-groups');
  const { mobileWidth } = useWidth();
  const isFirstRender = useIsFirstRender();
  const { searchParams } = useSearchParams();
  const orderStatusOptions = useOrderStatusOptions();
  const { data: maxSubtotalData } = useGetMaxSubtotal();
  const [isExporting, setIsExporting] = useState(false);
  const { translate, translateOptions } = useTranslation();
  const { isSeller, isStore, hasIntegrations } = useApplicationContext();
  const isInStoreEnvironment = useAppEnvironmentStore(selectIsInStoreEnvironment);

  const list = listProp ?? FilterPreferenceRecognizedLists.ORDERS;

  const { handleExportCsvs } = useExportMultipleOrdersCsv({
    selectedIds,
    selectedTab,
    customerIds,
  });

  const {
    shouldIgnoreDefaultFilters,
    defaultFilters: rawDefaultFilters,
    defaultFiltersByPrefix: rawDefaultFiltersByPrefix,
  } = useDefaultFiltersToList({
    list,
    filtersKeys: Object.values(ORDERS_FILTERS_KEYS),
    filterKeysPrefix: [
      CLASSIFICATIONS_PREFIX,
      ORDER_CONDITION_CLASSIFICATIONS_PREFIX,
      ORDER_CUSTOMER_CLASSIFICATIONS_PREFIX,
    ],
  });

  const defaultFilters = useMemo(() => {
    if (!isInStoreEnvironment || !rawDefaultFilters) {
      return rawDefaultFilters;
    }

    return Object.fromEntries(
      Object.entries(rawDefaultFilters).filter(
        ([key]) =>
          !BRAND_ENVIRONMENT_ONLY_FILTER_KEYS.includes(key) &&
          !BRAND_ENVIRONMENT_ONLY_FILTER_PREFIXES.some((prefix) => key.startsWith(prefix)),
      ),
    );
  }, [isInStoreEnvironment, rawDefaultFilters]);

  const defaultFiltersByPrefix = useMemo(() => {
    if (!isInStoreEnvironment) {
      return rawDefaultFiltersByPrefix;
    }

    return Object.fromEntries(
      Object.entries(rawDefaultFiltersByPrefix).filter(
        ([prefix]) => !BRAND_ENVIRONMENT_ONLY_FILTER_PREFIXES.includes(prefix),
      ),
    );
  }, [isInStoreEnvironment, rawDefaultFiltersByPrefix]);

  const queryLimit = mobileWidth ? 20 : 10;
  const archivedOrderParam = searchParams[ORDERS_FILTERS_KEYS.archivedOrder];

  const repository = filterOptionsRepository();
  const classificationTypeRepository = classificationTypesRepository();

  const {
    cleanFilter: cleanOrderIdsFilter,
    getSearchParamKeys: getSearchParamKeysOfOrderIdsFilter,
    ...orderIdsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findOrdersFilterOptions,
    queryKeyFn: getFindOrdersFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.ids],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'code',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.ids,
    formatLabel: (rawData) => `${rawData.code}\n${rawData.externalCode || '-'}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      createdAt: 'DESC',
    },
  });

  const {
    cleanFilter: cleanParentOrdersFilter,
    getSearchParamKeys: getSearchParamKeysOfParentOrdersFilter,
    ...parentOrdersFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findParentOrdersFilterOptions,
    queryKeyFn: getFindParentOrdersFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.baseCodes],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'code',
    paramKeyToSearchSpecifics: 'codes',
    key: ORDERS_FILTERS_KEYS.baseCodes,
    valueKey: 'code',
    formatLabel: (rawData) =>
      `${rawData.code}\n${translate(LangEnum.GLOBAL_SPLIT_COUNT, { quantity: rawData.splitCount })}`,
    formatChipLabel: (rawData) => rawData.code,
    defaultQueryParam: {
      limit: queryLimit,
      archivedOrder: archivedOrderParam,
    },
    order: {
      code: 'DESC',
    },
  });

  const {
    cleanFilter: cleanSuggestedCopiesFilter,
    getSearchParamKeys: getSearchParamKeysOfSuggestedCopiesFilter,
    ...suggestedCopiesFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findSuggestedCopyOrdersFilterOptions,
    queryKeyFn: getFindSuggestedCopiesFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.suggestedCopies],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'code',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.suggestedCopies,
    formatLabel: (rawData) => `${rawData.code}\n${rawData.externalCode || '-'}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      createdAt: 'DESC',
    },
  });

  const {
    cleanFilter: cleanOrderVouchersFilter,
    getSearchParamKeys: getSearchParamKeysOfOrderVouchersFilter,
    ...orderVouchersFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findOrderVouchersFilterOptions,
    queryKeyFn: getFindOrderVouchersFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.orderVouchers],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'nameOrIntegrationCode',
    paramKeyToSearchSpecifics: 'businessPolicyIds',
    key: ORDERS_FILTERS_KEYS.orderVouchers,
    formatLabel: (rawData) => `${rawData.name}\n${rawData.integrationCode ?? '-'}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanOrderSalesRepresentativesFilter,
    getSearchParamKeys: getSearchParamKeysOfOrderSalesRepresentativesFilter,
    ...orderSalesRepresentativesFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findSalesRepresentativesFilterOptions,
    queryKeyFn: getFindSalesRepresentativesFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.orderSalesRepresentative],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'nameOrCode',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.orderSalesRepresentative,
    isDisabled: canNotReadSalesRepresentatives,
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code || '-'}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanCustomerSalesRepresentativesFilter,
    getSearchParamKeys: getSearchParamKeysOfCustomerSalesRepresentativesFilter,
    ...customerSalesRepresentativesFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findSalesRepresentativesFilterOptions,
    queryKeyFn: getFindSalesRepresentativesFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.customerSalesRepresentative],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'nameOrCode',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.customerSalesRepresentative,
    isDisabled: canNotReadSalesRepresentatives,
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code || '-'}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanOrderSellersFilter,
    getSearchParamKeys: getSearchParamKeysOfOrderSellersFilter,
    ...orderSellersFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findSellersFilterOptions,
    queryKeyFn: getFindSellersFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.orderSeller],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.orderSeller,
    isDisabled: ability.cannot('read', 'sellers'),
    formatLabel: (rawData) => rawData.name || rawData.email,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanOrderCollectionsFilter,
    getSearchParamKeys: getSearchParamKeysOfOrderCollectionsFilter,
    ...orderCollectionsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCollectionsFilterOptions,
    queryKeyFn: getFindCollectionsFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.orderCollections],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.orderCollections,
    isDisabled: ability.cannot('read', 'collections'),
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanCustomersFilter,
    getSearchParamKeys: getSearchParamKeysOfCustomersFilter,
    ...customersFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCustomersFilterOptions,
    queryKeyFn: getFindCustomersFilterOptionsKey,
    defaultValues: defaultFilters?.[CUSTOMERS_IDS_KEYS[0]],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'customersIds',
    key: ORDERS_FILTERS_KEYS[CUSTOMER_FILTER_KEYS[0]],
    isDisabled: ability.cannot('read', 'customers'),
    formatLabel: (rawData) =>
      `${rawData.commercialName || '-'}\n${rawData.companyName || '-'}\n${rawData.cpfcnpj || '-'}`,
    defaultQueryParam: {
      limit: queryLimit,
      basedOnList: 'orders',
    },
    order: {
      createdAt: 'DESC',
    },
  });

  const {
    getSearchParamKeys: getSearchParamsKeysOfCustomersCodeFilter,
    cleanFilter: cleanCustomersCodeFilter,
    ...customersCodeFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCustomersFilterOptions,
    queryKeyFn: getFindCustomersFilterOptionsKey,
    defaultValues: defaultFilters?.[CUSTOMERS_IDS_KEYS[1]],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'customersIds',
    key: ORDERS_FILTERS_KEYS[CUSTOMER_FILTER_KEYS[1]],
    isDisabled: ability.cannot('read', 'customers'),
    formatLabel: (rawData) =>
      `${rawData.commercialName || '-'}\n${rawData.companyName || '-'}\n${rawData.code || '-'}`,
    defaultQueryParam: {
      limit: queryLimit,
      basedOnList: 'orders',
    },
    order: {
      createdAt: 'DESC',
    },
  });

  const {
    cleanFilter: cleanCustomerGroupsFilter,
    getSearchParamKeys: getSearchParamKeysOfCustomerGroupsFilter,
    ...customerGroupsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCustomerGroupsFilterOptions,
    queryKeyFn: getFindCustomerGroupsFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.customerGroupIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'name',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.customerGroupIds,
    isDisabled: canNotReadCustomerGroups,
    formatLabel: (raw) => raw.name,
    defaultQueryParam: { limit: queryLimit },
    order: { name: 'ASC' },
    valueKey: 'id',
  });

  const {
    cleanFilter: cleanCustomerCitiesFilter,
    getSearchParamKeys: getSearchParamKeysOfCustomerCitiesFilter,
    ...customerCitiesFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCitiesFilterOptions,
    queryKeyFn: getFindCitiesFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.customerCityIds],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'name',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.customerCityIds,
    isDisabled: ability.cannot('read', 'municipality'),
    formatLabel: (rawData) => `${rawData.name}\n${rawData.uf}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanCustomerStatesFilter,
    getSearchParamKeys: getSearchParamKeysOfCustomerStatesFilter,
    ...customerStatesFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findStatesFilterOptions,
    queryKeyFn: getFindStatesFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.customerStates],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'uf',
    paramKeyToSearchSpecifics: 'ufs',
    key: ORDERS_FILTERS_KEYS.customerStates,
    isDisabled: ability.cannot('read', 'municipality'),
    formatLabel: (rawData) => rawData.uf,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      uf: 'ASC',
    },
    valueKey: 'uf',
  });

  const {
    cleanFilter: cleanProductsFilter,
    getSearchParamKeys: getSearchParamKeysOfProductsFilter,
    ...productsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findProductsFilterOptions,
    queryKeyFn: getFindProductsFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.products],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'codeOrName',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.products,
    isDisabled: ability.cannot('read', 'products'),
    formatChipLabel: (rawData) =>
      `${rawData.code} ${rawData.referenceCode ? `(${rawData.referenceCode})` : ''} ${rawData.name}`,
    formatLabel: (rawData) =>
      `${rawData.name}\n ${rawData.code} ${rawData.referenceCode ? `(${rawData.referenceCode})` : ''}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanProductColorsFilter,
    getSearchParamKeys: getSearchParamKeysOfProductColorsFilter,
    ...productColorsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findColorsFilterOptions,
    queryKeyFn: getFindColorsFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.productColors],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.productColors,
    isDisabled: ability.cannot('read', 'colors'),
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      code: 'ASC',
    },
  });

  const {
    cleanFilter: cleanCollectionsFilter,
    getSearchParamKeys: getSearchParamKeysOfCollectionsFilter,
    ...collectionsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCollectionsFilterOptions,
    queryKeyFn: getFindCollectionsFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.collections],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.collections,
    isDisabled: ability.cannot('read', 'collections'),
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanProductCategoryFilter,
    getSearchParamKeys: getSearchParamKeysOfProductCategoryFilter,
    ...productCategoryFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCategoriesFilterOptions,
    queryKeyFn: getFindCategoriesFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.productCategory],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'nameOrCode',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.productCategory,
    isDisabled: ability.cannot('read', 'categories'),
    formatLabel: (rawData) => `${rawData.name}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
    aggregatingOptionParams: {
      aggregatingValueKey: 'groupId',
      aggregatingLabelKey: 'groupName',
    },
  });

  const {
    cleanFilter: cleanDeliveryWindowsFilter,
    getSearchParamKeys: getSearchParamKeysOfDeliveryWindowsFilter,
    ...deliveryWindowsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findDeliveryWindowsFilterOptions,
    queryKeyFn: getFindDeliveryWindowsFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.deliveryWindows],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'name',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.deliveryWindows,
    isDisabled: ability.cannot('read', 'delivery-windows'),
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanConsultantsFilter,
    getSearchParamKeys: getSearchParamKeysOfConsultantsFilter,
    ...consultantsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findConsultantsFilterOptions,
    queryKeyFn: getFindConsultantsFilterOptionsKey,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.consultants],
    shouldIgnoreDefaultFilters,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: ORDERS_FILTERS_KEYS.consultants,
    isDisabled: ability.cannot('read', 'consultants'),
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
    defaultQueryParam: {
      limit: queryLimit,
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanAvailabilityFilter,
    getSearchParamKeys: getSearchParamKeysOfAvailabilityFilter,
    ...availabilityFilter
  } = useMultiStaticOptionsSelector({
    key: ORDERS_FILTERS_KEYS.availability,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.availability],
    shouldIgnoreDefaultFilters,
    staticOptions: productAvailabilityOptions.map((av) => ({
      ...av,
      label: translate(av.label),
    })),
  });

  const {
    label: brandsFilterLabel,
    cleanFilter: cleanBrandsFilter,
    getSearchParamKeys: getSearchParamKeysOfBrandsFilter,
    brandsFilter,
  } = useBrandsFilter({
    filterKey: ORDERS_FILTERS_KEYS.brands,
    defaultFilters,
    shouldIgnoreDefaultFilters,
    queryLimit,
  });
  const isBrandsFilterVisible = isInStoreEnvironment;

  const orderCodeSearchFieldHook = useTextFilter({
    key: ORDERS_FILTERS_KEYS.code,
    isAutoApply: mobileWidth,
  });

  const dateFilter = useDateRangeFilterWithDefaultValues({
    defaultFilters,
    shouldIgnoreDefaultFilters,
    title: translate(LangEnum.REPORT_HEADER_ORDER_CREATE_DATE),
    filtersKey: {
      endDateKey: ORDERS_FILTERS_KEYS.endCreationDate,
      startDateKey: ORDERS_FILTERS_KEYS.startCreationDate,
      shortcutKey: ORDERS_FILTERS_KEYS.dateShortcut,
      shortcutStepKey: ORDERS_FILTERS_KEYS.dateShortcutStep,
    },
    toHide: includeDateFilter,
    fixed: includeDateFilter,
  });

  const {
    cleanFilter: cleanUpdatedDateFilter,
    getSearchParamKeys: getSearchParamKeysOfUpdatedDateFilter,
    ...updatedDateFilterArgs
  } = useDateRangeFilter({
    initialDateKey: ORDERS_FILTERS_KEYS.startUpdatedDate,
    finalDateKey: ORDERS_FILTERS_KEYS.endUpdatedDate,
    shortcutKey: ORDERS_FILTERS_KEYS.updatedDateShortcut,
    shortcutStepKey: ORDERS_FILTERS_KEYS.updatedDateShortcutStep,
    shouldIgnoreDefaultFilters,
    shortcutDefaultValue: defaultFilters?.[ORDERS_FILTERS_KEYS.updatedDateShortcut] as DateShortcut,
    shortcutStepDefaultValue: defaultFilters?.[
      ORDERS_FILTERS_KEYS.updatedDateShortcutStep
    ] as DateShortcut,
    initialDateDefaultValue: defaultFilters?.[ORDERS_FILTERS_KEYS.startUpdatedDate] as string,
    finalDateDefaultValue: defaultFilters?.[ORDERS_FILTERS_KEYS.endUpdatedDate] as string,
  });

  const {
    cleanFilter: cleanArchiveOrderFilter,
    getSearchParamKeys: getSearchParamKeysOfArchiveOrderFilter,
    ...archiveOrderFilter
  } = useRadioFilter({
    key: ORDERS_FILTERS_KEYS.archivedOrder,
    defaultValue: defaultFilters?.[ORDERS_FILTERS_KEYS.archivedOrder] as string,
    shouldIgnoreDefaultFilters,
    options: [{ value: 'true', label: translate(LangEnum.GLOBAL_SHOW_ARCHIVED_ORDERS) }],
  });

  const {
    cleanFilter: cleanMinAndMaxPriceFilter,
    getSearchParamKeys: getSearchParamKeysOfMinAndMaxPriceFilter,
    ...minAndMaxPriceFilter
  } = useNumberRangeFilter({
    type: NumberRangeFilterTypes.MONETARY,
    maxValueKey: ORDERS_FILTERS_KEYS.maximumPrice,
    minValueKey: ORDERS_FILTERS_KEYS.minimumPrice,
    maxAvailableValue: maxSubtotalData?.maxSubtotal,
    defaultMaxValue: defaultFilters?.[ORDERS_FILTERS_KEYS.maximumPrice]
      ? +defaultFilters?.[ORDERS_FILTERS_KEYS.maximumPrice]
      : undefined,
    defaultMinValue: defaultFilters?.[ORDERS_FILTERS_KEYS.minimumPrice]
      ? +defaultFilters?.[ORDERS_FILTERS_KEYS.minimumPrice]
      : undefined,
    shouldIgnoreDefaultFilters,
  });

  const availableOrderStatus = useMemo(() => {
    const { getConditions, isCustomRole } = ability;
    const supplierOrderStatus = getConditions()?.orderStatus;
    const defaultOrderStatus = getOrderStatusByTab(selectedTab);

    if (isCustomRole()) {
      return supplierOrderStatus || [];
    }
    return defaultOrderStatus;
  }, [selectedTab, ability]);

  const {
    cleanFilter: cleanOrderStatusFilter,
    getSearchParamKeys: getSearchParamKeysOfOrderStatusFilter,
    ...orderStatusFilter
  } = useMultiStaticOptionsSelector({
    key: ORDERS_FILTERS_KEYS.orderStatus,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.orderStatus],
    shouldIgnoreDefaultFilters,
    staticOptions: availableOrderStatus.length
      ? orderStatusOptions.filter(({ value }) => availableOrderStatus.includes(value))
      : orderStatusOptions,
  });

  const {
    cleanFilter: cleanOrderSignatureFilter,
    getSearchParamKeys: getSearchParamKeysOfOrderSignatureFilter,
    ...orderSignatureFilter
  } = useMultiStaticOptionsSelector({
    shouldIgnoreDefaultFilters,
    key: ORDERS_FILTERS_KEYS.orderContractSignature,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.orderContractSignature],
    staticOptions: orderSignatureLangEnumOptions.map((orderSignatureOption) => ({
      ...orderSignatureOption,
      label: translate(orderSignatureOption.label),
    })),
  });

  const {
    cleanFilter: cleanOrderPaymentFilter,
    getSearchParamKeys: getSearchParamKeysOfOrderPaymentFilter,
    ...orderPaymentFilter
  } = useMultiStaticOptionsSelector({
    shouldIgnoreDefaultFilters,
    key: ORDERS_FILTERS_KEYS.orderGatewayPaymentStatus,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.orderGatewayPaymentStatus],
    staticOptions: orderGatewayPaymentStatusOptions.map((orderPaymentOption) => ({
      ...orderPaymentOption,
      label: translate(orderPaymentOption.label),
    })),
  });

  const {
    cleanFilter: cleanIntegrationStatusFilter,
    getSearchParamKeys: getSearchParamKeysOfIntegrationStatusFilter,
    ...integrationStatusFilter
  } = useMultiStaticOptionsSelector({
    key: ORDERS_FILTERS_KEYS.integrationStatus,
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.integrationStatus],
    shouldIgnoreDefaultFilters,
    staticOptions: synchronizedTypesSelect.map((syncType) => ({
      ...syncType,
      label: translate(syncType.label),
    })),
  });

  const {
    cleanFilter: cleanCustomerStatusFilter,
    getSearchParamKeys: getSearchParamKeysOfCustomerStatusFilter,
    ...customerStatusFilter
  } = useMultiStaticOptionsSelector({
    defaultValues: defaultFilters?.[ORDERS_FILTERS_KEYS.customerStatus],
    shouldIgnoreDefaultFilters,
    key: ORDERS_FILTERS_KEYS.customerStatus,
    staticOptions: translateOptions(CustomerStatusEnum.values),
  });

  const options: FilterOptionV3[] = [
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.GLOBAL_ORDER_CODE),
      multiDynamicOptionsArgs: orderIdsFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanOrderIdsFilter,
      getSearchParamKeys: getSearchParamKeysOfOrderIdsFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'orders',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_PARENT_ORDER),
      multiDynamicOptionsArgs: parentOrdersFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanParentOrdersFilter,
      getSearchParamKeys: getSearchParamKeysOfParentOrdersFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'orders',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_ORDER_STATUS),
      hideSearchField: true,
      multiStaticOptionsArgs: orderStatusFilter,
      cleanFilter: cleanOrderStatusFilter,
      getSearchParamKeys: getSearchParamKeysOfOrderStatusFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'orders',
    },
    {
      ...dateFilter,
    },
    {
      type: PossibleFilters.DATE_RANGE,
      title: translate(LangEnum.REPORT_HEADER_ORDER_UPDATED_AT),
      dateRangeArgs: updatedDateFilterArgs,
      cleanFilter: cleanUpdatedDateFilter,
      getSearchParamKeys: getSearchParamKeysOfUpdatedDateFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA,
      toHide: isStore || !hasIntegrations,
      title: translate(LangEnum.ORDER_FILTER_INTEGRATION_STATUS),
      hideSearchField: true,
      multiStaticOptionsArgs: integrationStatusFilter,
      cleanFilter: cleanIntegrationStatusFilter,
      getSearchParamKeys: getSearchParamKeysOfIntegrationStatusFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'order-details',
    },
    {
      type: PossibleFilters.NUMBER_RANGE,
      title: translate(LangEnum.ORDER_FILTER_PRICE_RANGE_TITLE),
      numberRangeArgs: minAndMaxPriceFilter,
      cleanFilter: cleanMinAndMaxPriceFilter,
      getSearchParamKeys: getSearchParamKeysOfMinAndMaxPriceFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'order-details',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.GLOBAL_ORDER_COLLECTION),
      multiDynamicOptionsArgs: orderCollectionsFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanOrderCollectionsFilter,
      getSearchParamKeys: getSearchParamKeysOfOrderCollectionsFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'collections',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      toHide: isStore || isSeller,
      title: translate(LangEnum.ORDER_FILTER_ORDER_SALES_REPRESENTATIVES),
      multiDynamicOptionsArgs: orderSalesRepresentativesFilter,
      cleanFilter: cleanOrderSalesRepresentativesFilter,
      getSearchParamKeys: getSearchParamKeysOfOrderSalesRepresentativesFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'sales-representatives',
    },
    {
      type: PossibleFilters.RADIO,
      toHide: isStore,
      title: translate(LangEnum.GLOBAL_ARCHIVED_ORDER),
      radioArgs: archiveOrderFilter,
      cleanFilter: cleanArchiveOrderFilter,
      getSearchParamKeys: getSearchParamKeysOfArchiveOrderFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'order-details',
      customChipLabel: translate(LangEnum.GLOBAL_SHOWING_ONLY_ARCHIVED),
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA,
      title: translate(LangEnum.GLOBAL_ORDER_SIGNATURE),
      hideSearchField: true,
      multiStaticOptionsArgs: orderSignatureFilter,
      cleanFilter: cleanOrderSignatureFilter,
      getSearchParamKeys: getSearchParamKeysOfOrderSignatureFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'order-details',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_SUGGESTED_COPIES),
      label: translate(LangEnum.ORDER_FILTER_SUGGESTED_COPIES_LABEL),
      multiDynamicOptionsArgs: suggestedCopiesFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanSuggestedCopiesFilter,
      getSearchParamKeys: getSearchParamKeysOfSuggestedCopiesFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'orders',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_VOUCHERS),
      label: translate(LangEnum.ORDER_VOUCHERS_FILTER_LABEL),
      multiDynamicOptionsArgs: orderVouchersFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanOrderVouchersFilter,
      getSearchParamKeys: getSearchParamKeysOfOrderVouchersFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'orders',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA,
      title: translate(LangEnum.ORDER_PAYMENT_TITLE),
      multiStaticOptionsArgs: orderPaymentFilter,
      cleanFilter: cleanOrderPaymentFilter,
      getSearchParamKeys: getSearchParamKeysOfOrderPaymentFilter,
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'order-payments',
      hideSearchField: true,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_CREATOR,
      title: translate(LangEnum.ORDER_CONDITIONS_ORDER_CLASSIFICATION_LABEL),
      groupBy: LangEnum.GLOBAL_ORDER,
      resourceType: 'classifications',
      toHide: isInStoreEnvironment,
      getLabel: (values) => translate(LangEnum.CATALOGS_FILTER_CLASSIFICATION_TITLE, values),
      getSearchParamKeys: () => {
        const valuesKey = Object.keys(searchParams);
        const keys = valuesKey.filter((key) =>
          key.startsWith(ORDER_CONDITION_CLASSIFICATIONS_PREFIX),
        );
        return keys;
      },
      cleanFilter: clearFiltersCreatedByCreator,
      multiDynamicOptionsCreatorArgs: {
        filtersQueryFn: findOrderConditionsClassificationTypes,
        filtersQueryKey: getFindOrderConditionsClassificationTypesKey,
        filtersQueryParams: {
          limit: 100,
          skip: 0,
        },
        dynamicOptionQueryFn: repository.findClassificationsFilterOptions,
        dynamicOptionQueryKey: getFindClassificationsFilterOptionsKey,
        keys: {
          filterTitle: 'name',
          filterId: 'id',
          idsPrefix: ORDER_CONDITION_CLASSIFICATIONS_PREFIX,
          keyToRequestOptions: 'classificationTypeId',
        },
        hookArgs: {
          defaultValues: defaultFiltersByPrefix[ORDER_CONDITION_CLASSIFICATIONS_PREFIX],
          shouldIgnoreDefaultFilters,
          paramKeyToSearchMore: 'query',
          paramKeyToSearchSpecifics: 'ids',
          formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
          defaultQueryParam: {
            limit: queryLimit,
          },
          order: {
            name: 'ASC',
          },
        },
      },
    } as MultiDynamicOptionsCreatorFilter<
      Parameters<typeof findOrderConditionsClassificationTypes>[0],
      Awaited<ReturnType<typeof findOrderConditionsClassificationTypes>>,
      Parameters<typeof repository.findClassificationsFilterOptions>[0],
      Awaited<ReturnType<typeof repository.findClassificationsFilterOptions>>
    > & { groupBy: LangEnum },
    {
      toHide: isStore,
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.GLOBAL_CUSTOMER_CODE),
      multiDynamicOptionsArgs: customersCodeFilter,
      cleanFilter: cleanCustomersCodeFilter,
      getSearchParamKeys: getSearchParamsKeysOfCustomersCodeFilter,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customers',
    },
    {
      toHide: isStore,
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_CUSTOMER),
      label: translate(LangEnum.CUSTOMERS_FILTER_STORE_LABEL),
      multiDynamicOptionsArgs: customersFilter,
      cleanFilter: cleanCustomersFilter,
      getSearchParamKeys: getSearchParamKeysOfCustomersFilter,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customers',
    },
    {
      toHide: isStore || isSeller,
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_CUSTOMER_STATUS),
      hideSearchField: true,
      multiStaticOptionsArgs: customerStatusFilter,
      cleanFilter: cleanCustomerStatusFilter,
      getSearchParamKeys: getSearchParamKeysOfCustomerStatusFilter,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customers',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.GLOBAL_ORDER_SELLER),
      multiDynamicOptionsArgs: orderSellersFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanOrderSellersFilter,
      getSearchParamKeys: getSearchParamKeysOfOrderSellersFilter,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customers',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      toHide: canNotReadSalesRepresentatives,
      title: translate(LangEnum.GLOBAL_CUSTOMER_REPRESENTATIVE),
      multiDynamicOptionsArgs: customerSalesRepresentativesFilter,
      cleanFilter: cleanCustomerSalesRepresentativesFilter,
      getSearchParamKeys: getSearchParamKeysOfCustomerSalesRepresentativesFilter,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customers',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CUSTOMERS_FILTER_GROUP),
      toHide: canNotReadCustomerGroups,
      multiDynamicOptionsArgs: customerGroupsFilter,
      cleanFilter: cleanCustomerGroupsFilter,
      getSearchParamKeys: getSearchParamKeysOfCustomerGroupsFilter,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customers',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.GLOBAL_CONSULTANTS),
      multiDynamicOptionsArgs: consultantsFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanConsultantsFilter,
      getSearchParamKeys: getSearchParamKeysOfConsultantsFilter,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customers',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      toHide: isStore || isSeller,
      title: translate(LangEnum.ORDER_FILTER_CUSTOMER_STATE),
      multiDynamicOptionsArgs: customerStatesFilter,
      cleanFilter: cleanCustomerStatesFilter,
      getSearchParamKeys: getSearchParamKeysOfCustomerStatesFilter,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customers',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      toHide: isStore || isSeller,
      title: translate(LangEnum.ORDER_FILTER_CUSTOMER_CITY),
      multiDynamicOptionsArgs: customerCitiesFilter,
      cleanFilter: cleanCustomerCitiesFilter,
      getSearchParamKeys: getSearchParamKeysOfCustomerCitiesFilter,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customers',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_PRODUCTS),
      multiDynamicOptionsArgs: productsFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanProductsFilter,
      getSearchParamKeys: getSearchParamKeysOfProductsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCT,
      resourceType: 'products',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_CATEGORY),
      multiDynamicOptionsArgs: productCategoryFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanProductCategoryFilter,
      getSearchParamKeys: getSearchParamKeysOfProductCategoryFilter,
      groupBy: LangEnum.GLOBAL_PRODUCT,
      resourceType: 'categories',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA,
      title: translate(LangEnum.GLOBAL_AVAILABILITY),
      hideSearchField: true,
      multiStaticOptionsArgs: availabilityFilter,
      cleanFilter: cleanAvailabilityFilter,
      getSearchParamKeys: getSearchParamKeysOfAvailabilityFilter,
      groupBy: LangEnum.GLOBAL_PRODUCT,
      resourceType: 'products',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_PRODUCT_DELIVERY_WINDOWS),
      multiDynamicOptionsArgs: deliveryWindowsFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanDeliveryWindowsFilter,
      getSearchParamKeys: getSearchParamKeysOfDeliveryWindowsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCT,
      resourceType: 'delivery-windows',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.GLOBAL_PRODUCT_COLLECTION),
      multiDynamicOptionsArgs: collectionsFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanCollectionsFilter,
      getSearchParamKeys: getSearchParamKeysOfCollectionsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCT,
      resourceType: 'collections',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_PRODUCT_COLORS),
      multiDynamicOptionsArgs: productColorsFilter,
      toHide: isInStoreEnvironment,
      cleanFilter: cleanProductColorsFilter,
      getSearchParamKeys: getSearchParamKeysOfProductColorsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCT,
      resourceType: 'product-colors',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.GLOBAL_BRANDS),
      multiDynamicOptionsArgs: brandsFilter,
      toHide: true,
      fixed: isBrandsFilterVisible,
      cleanFilter: cleanBrandsFilter,
      getSearchParamKeys: getSearchParamKeysOfBrandsFilter,
      groupBy: LangEnum.GLOBAL_BRAND,
      resourceType: 'brands',
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_CREATOR,
      title: translate(LangEnum.GLOBAL_CLASSIFICATIONS),
      groupBy: LangEnum.GLOBAL_PRODUCT,
      resourceType: 'classifications',
      toHide: isInStoreEnvironment,
      getLabel: (values) => translate(LangEnum.CATALOGS_FILTER_CLASSIFICATION_TITLE, values),
      getSearchParamKeys: () => {
        const valuesKey = Object.keys(searchParams);
        const keys = valuesKey.filter((key) => key.startsWith(CLASSIFICATIONS_PREFIX));
        return keys;
      },
      cleanFilter: () => {
        clearFiltersCreatedByCreator();
      },
      multiDynamicOptionsCreatorArgs: {
        filtersQueryFn: classificationTypeRepository.findClassificationTypes,
        filtersQueryKey: getFindClassificationTypesKey,
        filtersQueryParams: {
          limit: 100,
          skip: 0,
          useAsFilter: true,
          entity: ClassificationTypeEntityEnum.PRODUCT_COLOR,
          'order[exhibition_order]': 'ASC',
        },
        dynamicOptionQueryFn: repository.findClassificationsFilterOptions,
        dynamicOptionQueryKey: getFindClassificationsFilterOptionsKey,
        keys: {
          filterTitle: 'name',
          filterId: 'id',
          idsPrefix: CLASSIFICATIONS_PREFIX,
          keyToRequestOptions: 'classificationTypeId',
        },
        hookArgs: {
          defaultValues: defaultFiltersByPrefix[CLASSIFICATIONS_PREFIX],
          shouldIgnoreDefaultFilters,
          paramKeyToSearchMore: 'query',
          paramKeyToSearchSpecifics: 'ids',
          formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
          defaultQueryParam: {
            limit: queryLimit,
          },
          order: {
            name: 'ASC',
          },
        },
      },
    } as MultiDynamicOptionsCreatorFilter<
      Parameters<typeof classificationTypeRepository.findClassificationTypes>[0],
      Awaited<ReturnType<typeof classificationTypeRepository.findClassificationTypes>>,
      Parameters<typeof repository.findClassificationsFilterOptions>[0],
      Awaited<ReturnType<typeof repository.findClassificationsFilterOptions>>
    > & { groupBy: LangEnum },
    {
      title: translate(LangEnum.GLOBAL_CUSTOMERS_CLASSIFICATIONS),
      type: PossibleFilters.OPTIONS_SELECTOR_CREATOR,
      groupBy: LangEnum.GLOBAL_CUSTOMER,
      resourceType: 'customer-classifications',
      getLabel: (values) => translate(LangEnum.GLOBAL_CUSTOMERS_CLASSIFICATIONS, values),
      getSearchParamKeys: () => {
        const valuesKey = Object.keys(searchParams);
        const keys = valuesKey.filter((key) =>
          key.startsWith(ORDER_CUSTOMER_CLASSIFICATIONS_PREFIX),
        );
        return keys;
      },
      cleanFilter: () => {
        clearFiltersCreatedByCreator();
      },
      multiDynamicOptionsCreatorArgs: {
        filtersQueryFn: classificationTypeRepository.findClassificationTypes,
        filtersQueryKey: getFindClassificationTypesKey,
        filtersQueryParams: {
          limit: 100,
          skip: 0,
          useAsFilter: true,
          entity: ClassificationTypeEntityEnum.CUSTOMER,
        },
        dynamicOptionQueryFn: repository.findClassificationsFilterOptions,
        dynamicOptionQueryKey: getFindClassificationsFilterOptionsKey,
        keys: {
          filterId: 'id',
          filterTitle: 'name',
          idsPrefix: ORDER_CUSTOMER_CLASSIFICATIONS_PREFIX,
          keyToRequestOptions: 'classificationTypeId',
        },
        hookArgs: {
          defaultValues: defaultFiltersByPrefix[ORDER_CUSTOMER_CLASSIFICATIONS_PREFIX],
          shouldIgnoreDefaultFilters,
          paramKeyToSearchMore: 'query',
          paramKeyToSearchSpecifics: 'ids',
          formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
          defaultQueryParam: {
            limit: queryLimit,
          },
          order: {
            createdAt: 'DESC',
          },
        },
      },
    } as MultiDynamicOptionsCreatorFilter<
      Parameters<typeof classificationTypeRepository.findClassificationTypes>[0],
      Awaited<ReturnType<typeof classificationTypeRepository.findClassificationTypes>>,
      Parameters<typeof repository.findClassificationsFilterOptions>[0],
      Awaited<ReturnType<typeof repository.findClassificationsFilterOptions>>
    >,
  ];

  const dateRangeFilterIndex = options.findIndex(
    (option) => option.title === translate(LangEnum.REPORT_HEADER_ORDER_CREATE_DATE),
  );

  const brandsFilterIndex = options.findIndex(
    (option) => option.title === translate(LangEnum.GLOBAL_BRANDS),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!isFirstRender) {
      cleanOrderStatusFilter();
    }
  }, [selectedTab]);

  useEffect(() => {
    if (!isFirstRender) {
      parentOrdersFilter.handleResetFilter();
    }
  }, [archivedOrderParam]);

  const dateRangeFilterComponent = includeDateFilter && (
    <DateRangeFilterToFilterV3
      label={LangEnum.REPORT_HEADER_ORDER_CREATE_DATE}
      filterIndex={dateRangeFilterIndex}
      dateRangeFilter={dateFilter.dateRangeArgs}
      excludedShortcuts={[DateShortcut.ALL_PERIOD]}
    />
  );

  const brandsFilterComponent = isBrandsFilterVisible && (
    <FixedFilterChip
      label={brandsFilterLabel}
      filterIndex={brandsFilterIndex}
      testId={FilterTestIds.BRANDS_FIXED_BUTTON}
    />
  );

  const columnsVisibility = (
    <ViewSettingsControl columns={columns} list={ColumnsPreferenceRecognizedLists.ORDERS} />
  );

  const fixedFilters = [dateRangeFilterComponent, brandsFilterComponent].filter(Boolean);

  const searchField = (
    <SearchField
      value={orderCodeSearchFieldHook.value}
      placeholder={LangEnum.GLOBAL_ORDER_CODE}
      testId={OrderListTestIds.SEARCH_CODE_FIELD}
      inputRef={orderCodeSearchFieldHook.textFieldRef}
      onChange={orderCodeSearchFieldHook.setValue}
      onClear={orderCodeSearchFieldHook.cleanFilter}
    />
  );

  const handleExport = async () => {
    setIsExporting(true);
    await handleExportCsvs();
    setIsExporting(false);
  };

  const dateRangeFilterConfig = {
    label: LangEnum.REPORT_HEADER_ORDER_CREATE_DATE,
    filterIndex: dateRangeFilterIndex,
    dateRangeArgs: dateFilter.dateRangeArgs,
  };

  const brandsFilterConfig = {
    isVisible: isBrandsFilterVisible,
    label: brandsFilterLabel,
    filterIndex: brandsFilterIndex,
  };

  const moreOptionsDrawerConfig = {
    columns,
    columnsPreferenceRecognizedList: ColumnsPreferenceRecognizedLists.ORDERS,
    isLoading: isExporting,
    isOpen: !!isDrawerOpen,
    handleClose: () => setIsDrawerOpen?.(false),
  };

  const exportConfig = {
    isExporting,
    handleExport,
  };

  return {
    list,
    options,
    mobileWidth,
    dateRangeFilter: dateRangeFilterConfig,
    brandsFilter: brandsFilterConfig,
    moreOptionsDrawer: moreOptionsDrawerConfig,
    exportConfig,
    columnsVisibility,
    fixedFilters,
    searchField,
    isSearchActive: orderCodeSearchFieldHook.hasSelectedValue,
    onClearSearch: orderCodeSearchFieldHook.cleanFilter,
  };
};

export type UseOrderFilterV2Result = ReturnType<typeof useOrderFilterV2>;
